import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Flame, Sparkles, Navigation, Users } from "lucide-react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { ConnectButton } from "@/components/ConnectButton";
import HeartLoader from "@/components/HeartLoader";

function MemberRow({ m }) {
  const initial = (m.name || "M").trim().charAt(0).toUpperCase();
  return (
    <div data-testid={`nearby-row-${m.user_id}`} className="flex items-center gap-3 rounded-xl border border-rud bg-white/5 px-3 py-2">
      {m.avatar_url ? (
        <img src={m.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover shrink-0" />
      ) : (
        <span className="w-8 h-8 rounded-full bg-brand/15 text-brand grid place-items-center text-sm font-medium shrink-0">{initial}</span>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <Link to={`/u/${m.user_id}`} className="text-sm truncate hover:text-brand transition-colors">{m.name}</Link>
          {m.kind_soul && (
            <span className="inline-flex items-center gap-0.5 text-[10px] text-brand bg-brand/15 rounded-full px-1.5 py-0.5 shrink-0">
              <Sparkles size={10} /> Kind Soul
            </span>
          )}
          {m.streak > 0 && (
            <span className="text-[10px] text-orange-400 flex items-center gap-0.5 shrink-0"><Flame size={11} className="fill-current" /> {m.streak}d</span>
          )}
        </div>
        <div className="text-[11px] text-sec truncate">{m.headline || m.city || "My Kind member"}</div>
      </div>
      <ConnectButton userId={m.user_id} size="xs" />
    </div>
  );
}

export function NearbyMembers() {
  const { user } = useApp();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    api.get("/members/nearby")
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [user?.user_id]);

  if (!user) return null;
  if (loading) return <div className="rud-card rounded-2xl p-6 mt-4 text-center" data-testid="nearby-loading"><HeartLoader /></div>;
  if (!data) return null;

  if (!data.has_location) {
    return (
      <div className="rud-card rounded-2xl p-4 mt-4" data-testid="nearby-no-location">
        <div className="flex items-center gap-2 mb-1"><MapPin size={16} className="text-brand" /><h3 className="font-serif text-lg">Members near you</h3></div>
        <p className="text-xs text-sec">Set your location to discover fellow My Kind members in your city and country.</p>
      </div>
    );
  }

  const total = (data.in_city?.length || 0) + (data.in_country?.length || 0);
  if (total === 0) {
    return (
      <div className="rud-card rounded-2xl p-4 mt-4" data-testid="nearby-empty">
        <div className="flex items-center gap-2 mb-1"><MapPin size={16} className="text-brand" /><h3 className="font-serif text-lg">Members near you</h3></div>
        <p className="text-xs text-sec">You're one of the first here in {data.city || data.country_name || "your area"}. Invite friends to grow your kind community 🌱</p>
      </div>
    );
  }

  return (
    <div className="rud-card rounded-2xl p-4 mt-4" data-testid="nearby-members">
      <div className="flex items-center gap-2 mb-3">
        <MapPin size={16} className="text-brand" />
        <h3 className="font-serif text-lg">Members near you</h3>
        <span className="text-[11px] text-sec">— say hi & connect 🤝</span>
      </div>

      {data.in_city?.length > 0 && (
        <div className="mb-4" data-testid="nearby-in-city">
          <div className="flex items-center gap-1.5 text-brand text-xs uppercase tracking-widest mb-2">
            <Navigation size={13} /> In {data.city}
          </div>
          <div className="space-y-1.5">
            {data.in_city.map((m) => <MemberRow key={m.user_id} m={m} />)}
          </div>
        </div>
      )}

      {data.in_country?.length > 0 && (
        <div data-testid="nearby-in-country">
          <div className="flex items-center gap-1.5 text-sec text-xs uppercase tracking-widest mb-2">
            <Users size={13} /> Across {data.country_name || data.country_code}
          </div>
          <div className="space-y-1.5">
            {data.in_country.map((m) => <MemberRow key={m.user_id} m={m} />)}
          </div>
        </div>
      )}
    </div>
  );
}

export default NearbyMembers;
